import React, { useEffect, useState } from 'react';
import { resumeService } from '../services/api';
import type { ResumeAnalytics } from '../types';
import { BarChart3, Eye, Download, Globe, Monitor, MousePointerClick, X } from 'lucide-react';

interface Props {
  resumeId: number;
  onClose: () => void;
}

const ResumeAnalyticsModal: React.FC<Props> = ({ resumeId, onClose }) => {
  const [data, setData] = useState<ResumeAnalytics | null>(null);

  useEffect(() => {
    resumeService
      .getResumeAnalytics(resumeId)
      .then((response) => setData(response.data))
      .catch(() => setData(null));
  }, [resumeId]);

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <h3 className="font-display font-semibold text-foreground flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-brand-500" />
            Resume Analytics
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-foreground rounded-btn hover:bg-zinc-50 transition-colors"
            aria-label="Close analytics"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-5">
          {!data ? (
            <div className="text-sm text-zinc-400 py-8 text-center">Loading analytics...</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-brand-50 rounded-card p-4 flex items-center gap-3">
                  <Eye className="w-5 h-5 text-brand-500" />
                  <div>
                    <p className="text-2xl font-display font-semibold text-foreground">{data.views}</p>
                    <p className="text-xs text-zinc-500">Views</p>
                  </div>
                </div>
                <div className="bg-emerald-50 rounded-card p-4 flex items-center gap-3">
                  <Download className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="text-2xl font-display font-semibold text-foreground">{data.downloads}</p>
                    <p className="text-xs text-zinc-500">Downloads</p>
                  </div>
                </div>
              </div>

              {data.topReferrers.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                    <Globe className="w-3.5 h-3.5 text-zinc-400" />
                    Top Traffic Sources
                  </h4>
                  <ul className="space-y-1.5">
                    {data.topReferrers.map((item) => (
                      <li key={item.referrer} className="flex items-center justify-between text-sm">
                        <span className="text-zinc-600">{item.referrer}</span>
                        <span className="font-medium text-foreground">{item.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {data.byOs.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                    <Monitor className="w-3.5 h-3.5 text-zinc-400" />
                    Operating Systems
                  </h4>
                  <ul className="space-y-1.5">
                    {data.byOs.map((item) => (
                      <li key={item.os} className="flex items-center justify-between text-sm">
                        <span className="text-zinc-600">{item.os}</span>
                        <span className="font-medium text-foreground">{item.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {data.byBrowser.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                    <MousePointerClick className="w-3.5 h-3.5 text-zinc-400" />
                    Browsers
                  </h4>
                  <ul className="space-y-1.5">
                    {data.byBrowser.map((item) => (
                      <li key={item.browser} className="flex items-center justify-between text-sm">
                        <span className="text-zinc-600">{item.browser}</span>
                        <span className="font-medium text-foreground">{item.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {data.topReferrers.length === 0 &&
                data.byOs.length === 0 &&
                data.byBrowser.length === 0 && (
                  <p className="text-sm text-zinc-400 text-center py-2">
                    No traffic recorded yet. Share your public link to start collecting analytics.
                  </p>
                )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResumeAnalyticsModal;
